import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { Table, Card, Tag, Space, Button, Tooltip, message, Modal, Input, Select, Row, Col, Statistic } from 'antd';
import { EditOutlined, DeleteOutlined, DollarOutlined, SearchOutlined } from '@ant-design/icons';
import { getAllOverpayments, deleteOverpayment } from '../../api/overpayment';
import { getAllPartners } from '../../api/accounting';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Simple local formatter that shows the stored amount with its currency symbol,
// without any conversion logic or external API calls.
const formatCurrencyPlain = (amount, currency) => {
  const value = parseFloat(amount || 0);
  const symbol = currency || "$";
  const dp = getDecimalPrecision();
  if (Number.isNaN(value)) return `${symbol} ${(0).toFixed(dp)}`;
  return `${symbol} ${value.toFixed(dp)}`;
};

const OverPaymentAllocation = forwardRef(
  ({ filters, selectedBrand, onAffectOverpayment, onCreateOverpayment }, ref) => {
  const [loading, setLoading] = useState(false);
  const [overpayments, setOverpayments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(500);
  const [totalItems, setTotalItems] = useState(0);
  const [summary, setSummary] = useState({
    count: 0,
    total_amount: 0,
    allocated_amount: 0
  });
  const navigate = useNavigate();

  // Fetch partners for dropdown
  const fetchPartners = useCallback(async () => {
    setLoadingPartners(true);
    try {
      const response = await getAllPartners();
      
      // Handle different response formats
      if (Array.isArray(response)) {
        setPartners(response);
      } else if (response && Array.isArray(response.data)) {
        setPartners(response.data);
      } else if (response && response.results && Array.isArray(response.results)) {
        setPartners(response.results);
      } else {
        console.error('Invalid partners response format:', response);
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      message.error('Failed to load partners');
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const fetchOverpayments = useCallback(async () => {
    setLoading(true);
    try {
      // Create API-compatible filter object
      const apiFilters = {};
      
      // Handle date range
      if (filters.dateRange && filters.dateRange.length === 2) {
        if (filters.dateRange[0]) {
          apiFilters.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        }
        if (filters.dateRange[1]) {
          apiFilters.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }
      }
      
      // Handle partner type filter
      if (filters.partnerType && filters.partnerType !== 'all') {
        apiFilters.partner_type = filters.partnerType;
      }
      
      // Handle brand filter
      if (selectedBrand) {
        apiFilters.brand = selectedBrand.id;
      }
      
      // Add partner filter if selected
      if (selectedPartner) {
        apiFilters.partner = selectedPartner;
      }
      
      // Add search query if provided
      if (searchQuery && searchQuery.trim() !== '') {
        apiFilters.name = searchQuery.trim();
      }
      
      const response = await getAllOverpayments(apiFilters);
      
      // Handle Axios response format
      const responseData = response.data ? response : { data: response };
      
      // Check if response is paginated (has results property)
      let overpaymentData = [];
      
      if (responseData.data && responseData.data.results) {
        overpaymentData = responseData.data.results;
        setTotalItems(responseData.data.total || 0);
        if (responseData.data.summary) {
          setSummary(responseData.data.summary);
        }
      } else if (response && response.results) {
        overpaymentData = response.results;
        setTotalItems(response.total || 0);
        if (response.summary) {
          setSummary(response.summary);
        }
      } else if (Array.isArray(responseData.data)) {
        overpaymentData = responseData.data;
      } else if (Array.isArray(response)) {
        overpaymentData = response;
      }
      
      if (!overpaymentData || !Array.isArray(overpaymentData)) {
        message.error('Received invalid data format from server');
        setOverpayments([]);
        return;
      }
      
      // Transform the data to match our component's expected format
      const formattedOverpayments = overpaymentData.map(overpayment => {
        // Calculate allocation percentage
        const total = parseFloat(overpayment.amount || 0);
        const remaining = parseFloat(overpayment.remaining_amount || 0);
        const allocatedAmount = total - remaining;
        const percentAllocated = total > 0 ? (allocatedAmount / total) * 100 : 0;
        
        // Determine allocation status
        let allocationStatus;
        if (percentAllocated >= 99.99) {
          allocationStatus = { type: 'fully_allocated', label: 'Fully Allocated', color: 'success' };
        } else if (percentAllocated > 0) {
          allocationStatus = { type: 'partially_allocated', label: 'Partially Allocated', color: 'warning' };
        } else {
          allocationStatus = { type: 'unallocated', label: 'Unallocated', color: 'default' };
        }
        
        return {
          id: overpayment.id,
          partner: overpayment.partner_name || 'Unknown',
          transaction_reference: overpayment.transaction_reference || '',
          created_date: overpayment.created_date,
          total: parseFloat(overpayment.amount || 0),
          allocated_amount: allocatedAmount,
          remaining_amount: remaining,
          // Keep the original data for reference
          originalData: overpayment,
          // Use allocations from the API format
          allocations: overpayment.allocations?.map(allocation => ({
            id: allocation.id,
            amount: allocation.amount,
            allocation_date: allocation.allocation_date,
            invoice_number: allocation.invoice_number || '',
          })) || [],
          allocationStatus: allocationStatus,
          percentAllocated: percentAllocated,
          // Store the currency for display
          currency: overpayment.currency || '$',
          notes: overpayment.notes
        };
      });
      
      setOverpayments(formattedOverpayments);
    } catch (error) {
      console.error('Error fetching overpayments:', error);
      message.error(`Failed to fetch overpayments: ${error.message || 'Unknown error'}`);
      setOverpayments([]);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedBrand, searchQuery, selectedPartner]);

  // Fetch overpayments when filters change
  useEffect(() => {
    fetchOverpayments();
  }, [fetchOverpayments, filters, selectedBrand]);

  // Handle edit overpayment
  const handleEditOverpayment = (overpaymentId) => {
    if (onCreateOverpayment) {
      onCreateOverpayment(overpaymentId);
    } else {
      navigate(`/accounting/payment-allocation/create-overpayment?id=${overpaymentId}`);
    }
  };

  // Handle delete overpayment
  const handleDeleteOverpayment = async (overpaymentId) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this overpayment? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          await deleteOverpayment(overpaymentId);
          message.success('Overpayment deleted successfully');
          fetchOverpayments(); // Refresh the list
        } catch (error) {
          console.error('Error deleting overpayment:', error);
          message.error('Failed to delete overpayment');
        }
      },
    });
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle partner selection
  const handlePartnerChange = (value) => {
    setSelectedPartner(value);
    setCurrentPage(1); // Reset to first page when changing partner
  };

  // Handle pagination
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
  };

  // Define table columns
  const columns = [
    {
      title: 'Partner',
      dataIndex: 'partner',
      key: 'partner',
      sorter: (a, b) => a.partner.localeCompare(b.partner),
      render: (text, record) => (
        <a onClick={() => handleEditOverpayment(record.id)}>{text}</a>
      ),
    },
    {
      title: 'Transaction Ref',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      sorter: (a, b) => (a.transaction_reference || '').localeCompare(b.transaction_reference || ''),
    },
    {
      title: 'Date',
      dataIndex: 'created_date',
      key: 'created_date',
      sorter: (a, b) => moment(a.created_date).unix() - moment(b.created_date).unix(),
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Type',
      key: 'transaction_type',
      width: 80,
      render: (_, record) => {
        // Check originalData for transaction_detail/detail field
        const detail = record.originalData?.transaction_detail || record.originalData?.detail;
        if (detail === 'DEBIT') {
          return <Tag color="red">Debit</Tag>;
        } else if (detail === 'CREDIT') {
          return <Tag color="green">Credit</Tag>;
        }
        // Fallback: infer from amount
        // Positive = DEBIT (money you paid out, e.g., overpaid a vendor)
        // Negative = CREDIT (money received, e.g., customer overpaid you)
        const amount = parseFloat(record.total || 0);
        if (amount > 0) {
          return <Tag color="red">Debit</Tag>;
        } else if (amount < 0) {
          return <Tag color="green">Credit</Tag>;
        }
        return <Tag>-</Tag>;
      },
      sorter: (a, b) => {
        const aType = a.originalData?.transaction_detail || a.originalData?.detail || (a.total > 0 ? 'DEBIT' : 'CREDIT');
        const bType = b.originalData?.transaction_detail || b.originalData?.detail || (b.total > 0 ? 'DEBIT' : 'CREDIT');
        return aType.localeCompare(bType);
      },
    },
    {
      title: 'Total Amount',
      dataIndex: 'total',
      key: 'total',
      sorter: (a, b) => a.total - b.total,
      render: (total, record) => {
        const currency =
          record?.currency || record?.originalData?.currency || '$';
        return formatCurrencyPlain(total || 0, currency);
      },
    },
    {
      title: 'Allocated',
      dataIndex: 'allocated_amount',
      key: 'allocated_amount',
      sorter: (a, b) => a.allocated_amount - b.allocated_amount,
      render: (allocated, record) => {
        const currency =
          record?.currency || record?.originalData?.currency || '$';
        return formatCurrencyPlain(allocated || 0, currency);
      },
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_amount',
      key: 'remaining_amount',
      sorter: (a, b) => a.remaining_amount - b.remaining_amount,
      render: (remaining, record) => {
        const currency =
          record?.currency || record?.originalData?.currency || '$';
        return formatCurrencyPlain(remaining || 0, currency);
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.allocationStatus.color}>
          {record.allocationStatus.label}
        </Tag>
      ),
      sorter: (a, b) => a.allocationStatus.type.localeCompare(b.allocationStatus.type),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Delete">
            <Button 
              type="danger" 
              icon={<DeleteOutlined />} 
              size="small" 
              onClick={() => handleDeleteOverpayment(record.id)}
            />
          </Tooltip>
          <Tooltip title="Allocate Overpayment">
            <Button 
              type="default" 
              icon={<DollarOutlined />} 
              size="small" 
              onClick={() => onAffectOverpayment ? onAffectOverpayment(record.id) : navigate(`/accounting/payment-allocation/overpayment-allocation?overpaymentId=${record.id}`)}
             />
          </Tooltip>
        </Space>
      ),
    },
  ];


  return (
    <div className="overpayments-page">
      <Card title="Overpayments List"  >
        <Table
          columns={columns}
          dataSource={overpayments}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
});
OverPaymentAllocation.displayName = 'OverPaymentAllocation';

export default OverPaymentAllocation;